import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import ReactInputMask from "react-input-mask";
import env from "react-dotenv";

// Api
import { getEventById, getEventSeatMap } from "../../api/plan";
import { createTicket, getOrderStatus } from "../../api/subscription";
import { validateVoucher } from "../../api/vouchers";

// Components
import Layout from "../../components/Layout";
import TicketInfoModal from "./TicketInfoModal";
import TermsAndConditionsModal from "./TermsConditionsModal";
import PaymentSuccessModal from "./PaymentSuccessModal";
import PaymentErrorModal from "./PaymentErrorModal";
import PaymentConflictModal from "./PaymentConflictModal";
import SeatPickerModal, { MAX_SEATS_PER_ORDER } from "../../components/SeatPickerModal";
import SearchableSelect from "../../components/SearchableSelect";
import { seatKey, unitLabel } from "../../components/SeatMap";
import { buildSeatPricing } from "../../components/SeatMap/pricing";
import { photoUrl, photoSrcSet } from "../../utils/photo";

// Images
import img1 from '../../assets/images/6843fecabc4c4263bf677a06.jpg';
import img2 from '../../assets/images/6810d0862c4130f769b80d6e.jpg';
import img3 from '../../assets/images/68666ca1ff1f250645e93163.jpg';
import img4 from '../../assets/images/68beb0fd43fa9f316940d9d7.jpg';
import img5 from '../../assets/images/68d51a255cacec7657fdc0ca.jpg';
import img6 from '../../assets/images/6914da163ebb52509b87c580.jpg';
import img7 from '../../assets/images/69656430b0baf7f5461a284d.jpg';
import img8 from '../../assets/images/6974b2cfb0baf7f5461a2854.jpg';
import img9 from '../../assets/images/69a6942b4904b10a800d0a48.jpg';
import img10 from '../../assets/images/69b6eff480bd832f37e9d58e.jpg';
import img11 from '../../assets/images/69b6f16680bd832f37e9d596.jpg';

const AZ_MONTHS = [
  'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'İyun',
  'İyul', 'Avqust', 'Sentyabr', 'Oktyabr', 'Noyabr', 'Dekabr'
];

export const formatEventDate = (dateString) => {
  if (!dateString) return '';
  // If already formatted (no 'T' or '-' pattern), return as-is
  if (typeof dateString === 'string' && !dateString.includes('T') && !/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    return dateString;
  }
  // Parse as UTC then extract UTC parts to avoid timezone shifts
  const d = new Date(dateString);
  const day = d.getUTCDate();
  const month = AZ_MONTHS[d.getUTCMonth()];
  const year = d.getUTCFullYear();
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  return `${day} ${month} ${year}, ${hours}:${minutes}`;
};

export const imageMap = {
  '6843fecabc4c4263bf677a06': img1,
  '6810d0862c4130f769b80d6e': img2,
  '68666ca1ff1f250645e93163': img3,
  '68beb0fd43fa9f316940d9d7': img4,
  '68d51a255cacec7657fdc0ca': img5,
  '6914da163ebb52509b87c580': img6,
  '69656430b0baf7f5461a284d': img7,
  '6974b2cfb0baf7f5461a2854': img8,
  '69a6942b4904b10a800d0a48': img9,
  '69b6eff480bd832f37e9d58e': img10,
  '69b6f16680bd832f37e9d596': img11,
};

// Ticket type descriptions are rich text; the buy page shows them as one short line
const htmlToText = (html) =>
  new DOMParser().parseFromString(String(html || ""), "text/html").body.textContent.replace(/\s+/g, " ").trim();

const InputField = ({ label, hint, children }) => (
  <div>
    <label className="flex items-baseline gap-1.5 mb-2">
      <span className="text-sm font-semibold text-surface-900">{label}</span>
      {hint && <span className="text-xs text-surface-400 font-normal">{hint}</span>}
    </label>
    {children}
  </div>
);

const inputClasses = "w-full border-0 bg-surface-50 rounded-2xl px-4 py-3.5 text-surface-900 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-accent-500/40 focus:bg-white transition-all duration-200 text-sm";

const PhotoLightbox = ({ photos, index, onClose, onChangeIndex }) => {
  const total = photos.length;
  const prev = () => onChangeIndex(index === 0 ? total - 1 : index - 1);
  const next = () => onChangeIndex(index === total - 1 ? 0 : index + 1);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={onClose}>
      <button
        onClick={onClose}
        className="fixed top-4 right-4 z-50 text-white/70 hover:text-white text-sm font-medium"
      >
        Bağla ✕
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); prev(); }}
        className="fixed left-4 top-1/2 -translate-y-1/2 z-50 w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center transition"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); next(); }}
        className="fixed right-4 top-1/2 -translate-y-1/2 z-50 w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center transition"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
      </button>
      <div className="relative max-w-5xl w-full mx-16" onClick={(e) => e.stopPropagation()}>
        <img
          src={photos[index]}
          alt=""
          className="w-full max-h-[85vh] object-contain rounded-lg"
        />
        {total > 1 && (
          <div className="flex justify-center gap-1.5 mt-4">
            {photos.map((_, i) => (
              <button
                key={i}
                onClick={() => onChangeIndex(i)}
                className={`w-2 h-2 rounded-full transition ${i === index ? 'bg-white w-5' : 'bg-white/40'}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const PlanDetailsPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const payment = searchParams.get('payment');
  const ticketToken = searchParams.get('ticketToken');
  // Tickets live on our own domain, like the links in the ticket email
  const ticketUrl = ticketToken
    ? `${env.TICKET_URL || "https://bilet.tixy.az"}/ticket/view/${ticketToken}`
    : null;
  const navigate = useNavigate();
  const [eventData, setEventData] = useState({});
  const isPast = useMemo(() => {
    const d = eventData?.data?.endDate || eventData?.data?.date;
    if (!d) return false;
    // date field may be formatted string — only parse if it looks like ISO
    const parsed = new Date(d);
    return !isNaN(parsed) && parsed < new Date();
  }, [eventData]);
  const allPhotos = useMemo(() => {
    const cover = eventData?.data?.photo || imageMap[eventData?.data?._id];
    const gallery = eventData?.data?.photos || [];
    return cover ? [cover, ...gallery] : gallery;
  }, [eventData]);
  const [isPageLoading, setIsPageLoading] = useState(true);
  // Width / height of the cover photo, to decide how the banner shows it
  const [coverRatio, setCoverRatio] = useState(null);
  const [isTicketInfoModalOpen, setIsTicketInfoModalOpen] = useState(false);
  const [isTermsConditionsModalOpen, setIsTermsConditionsModalOpen] = useState(false);
  const [isPaymentSuccessModalOpen, setIsPaymentSuccessModalOpen] = useState(payment === 'success');
  // A paid order can still fail: somebody else's payment may have reached the
  // same seat first, in which case no ticket exists and the money goes back.
  const [cancelledOrder, setCancelledOrder] = useState(null);
  const [isPaymentErrorModalOpen, setIsPaymentErrorModalOpen] = useState(payment === 'error');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Seat selection states (only used when the event has seat selection)
  const [seatMap, setSeatMap] = useState(null);
  const [seatMapError, setSeatMapError] = useState(false);
  const [selectedSeats, setSelectedSeats] = useState([]);
  // Tickets asked for when the type is sold without a seat
  const [quantity, setQuantity] = useState(1);
  const [isSeatPickerOpen, setIsSeatPickerOpen] = useState(false);

  // Voucher states
  const [voucherData, setVoucherData] = useState(null);
  const [voucherError, setVoucherError] = useState(null);
  const [isValidatingVoucher, setIsValidatingVoucher] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    formState: { isValid, isSubmitting, errors },
    watch
  } = useForm();

  const selectedTicket = watch("ticket");
  const voucherCode = watch("voucherCode");

  const priceMapping = useMemo(() => {
    return (eventData?.data?.ticketTypes || []).reduce((acc, item) => {
      acc[item._id] = item;
      return acc;
    }, {});
  }, [eventData]);

  const isSeatMode = !!(eventData?.data?.hasSeatSelection && eventData?.data?.venueId);
  // The organizer's own question, shown only when it has a label and options
  const customField = eventData?.data?.customField?.label && eventData?.data?.customField?.options?.length
    ? eventData.data.customField
    : null;

  // Which ticket types and prices apply to each seat (by zone and row section)
  const seatPricing = useMemo(
    () => buildSeatPricing(seatMap?.venue, eventData?.data?.ticketTypes, eventData?.data?.seatPlan),
    [seatMap, eventData]
  );

  const zoneOf = (code) => seatMap?.venue?.zones?.find((zone) => zone.code === code);

  // Ticket types without seats on the map (e.g. tables): bought without a seat,
  // limited by their count ("Limit"); the description is shown as their info line
  const seatlessTypes = useMemo(() => {
    const onMap = new Set(seatPricing.priceList.map(({ key }) => key));
    return (eventData?.data?.ticketTypes || [])
      .filter((type) => !onMap.has(type._id))
      .map((type) => ({
        ...type,
        info: htmlToText(type.description?.az),
        isSoldOut: Boolean(type.count) && (type.soldCount || 0) >= type.count,
      }));
  }, [eventData, seatPricing]);
  const isMapChoice = selectedSeats.length > 0;

  const ticketDetails = selectedTicket ? priceMapping[selectedTicket] : null;
  // A table ticket is already a group, so it is always bought one at a time
  const isTableTicket = Boolean(ticketDetails?.isTable && ticketDetails?.capacity > 1);
  const orderCount = isMapChoice ? selectedSeats.length : (isTableTicket ? 1 : quantity);

  const chooseSeatlessType = (type) => {
    setSelectedSeats([]);
    setQuantity(1);
    setValue("seatNumber", "", { shouldValidate: true });
    setValue("ticket", type._id, { shouldValidate: true });
  };

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isAndroid = /Android/i.test(navigator.userAgent);

  const fetchEventData = useCallback(async () => {
    setIsPageLoading(true);
    const response = await getEventById(id);
    setEventData(response?.data);
    setIsPageLoading(false);
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchEventData();
    }
  }, [fetchEventData, id]);

  // Opening an event from a scrolled list keeps the list's scroll position;
  // always start the event page at the top, before it paints
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const fetchSeatMap = useCallback(async () => {
    setSeatMapError(false);
    try {
      const response = await getEventSeatMap(id);
      setSeatMap(response?.data?.data);
    } catch (error) {
      setSeatMapError(true);
    }
  }, [id]);

  useEffect(() => {
    if (isSeatMode) {
      fetchSeatMap();
    }
  }, [fetchSeatMap, isSeatMode]);

  // Drop any seat that turned out to be sold meanwhile; the others stay
  useEffect(() => {
    if (!selectedSeats.length || !seatMap) return;
    const taken = new Set((seatMap.takenSeats || []).map(({ zone, number }) => seatKey(zone, number)));
    const left = selectedSeats.filter(({ zone, number }) => !taken.has(seatKey(zone, number)));
    if (left.length === selectedSeats.length) return;
    setSelectedSeats(left);
    setValue("seatNumber", left[0]?.number ?? "", { shouldValidate: true });
    if (!left.length) setValue("ticket", "", { shouldValidate: true });
  }, [seatMap, selectedSeats, setValue]);

  const scrollToElement = (elementId) => {
    document.getElementById(elementId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Loaded with the page so the popup opens at once; refreshed on open for sold seats
  const openSeatPicker = () => {
    setIsSeatPickerOpen(true);
    fetchSeatMap();
  };

  const closeSeatPicker = useCallback(() => setIsSeatPickerOpen(false), []);

  const handleConfirmSeats = (seats) => {
    setSelectedSeats(seats);
    setValue("seatNumber", seats[0]?.number ?? "", { shouldValidate: true });
    setValue("ticket", seats[0]?.ticketTypeId || "", { shouldValidate: true });
    setIsSeatPickerOpen(false);
  };

  const handleVoucherValidation = useCallback(async (code, ticketId, count = 1) => {
    if (!code || !code.trim()) {
      setVoucherData(null);
      setVoucherError(null);
      return;
    }

    if (!ticketId) {
      setVoucherError(isSeatMode ? "Əvvəlcə yer seçin" : "Əvvəlcə bilet növü seçin");
      return;
    }

    setIsValidatingVoucher(true);
    setVoucherError(null);

    try {
      const ticketDetails = priceMapping[ticketId];
      const orderAmount = (ticketDetails?.salePrice || ticketDetails?.price || 0) * count;

      const response = await validateVoucher({
        code: code.trim(),
        eventId: id,
        ticketTypeId: ticketId,
        orderAmount
      });

      if (response.data.success) {
        setVoucherData(response.data.data);
        setVoucherError(null);
      } else {
        setVoucherData(null);
        setVoucherError(response.data.message || "Promokod yalnışdır");
      }
    } catch (error) {
      setVoucherData(null);
      setVoucherError(error.response?.data?.message || "Promokod yoxlanılarkən xəta baş verdi");
    } finally {
      setIsValidatingVoucher(false);
    }
  }, [id, priceMapping, isSeatMode]);

  useEffect(() => {
    if (voucherCode && voucherCode.trim()) {
      handleVoucherValidation(voucherCode, selectedTicket, orderCount);
    } else {
      setVoucherData(null);
      setVoucherError(null);
    }
  }, [handleVoucherValidation, selectedTicket, voucherCode, orderCount]);

  const onSubmit = async (data) => {
    let response;
    try {
      response = await createTicket({
        eventId: id,
        ticket: data?.ticket,
        name: data?.name,
        email: data?.email,
        phone: data?.phone,
        paymentMethod: data?.paymentMethod,
        voucherCode: data?.voucherCode || null,
        customValue: customField ? data?.customValue || null : undefined,
        seatNumber: isSeatMode && isMapChoice ? selectedSeats[0]?.number : undefined,
        seatZone: isSeatMode && isMapChoice ? selectedSeats[0]?.zone : undefined,
        seats: isSeatMode && isMapChoice ? selectedSeats.map(({ zone, number }) => ({ zone, number })) : undefined,
        quantity: isMapChoice ? undefined : orderCount,
      });
    } catch (error) {
      setError("root.serverError", { type: "server", message: error?.response?.data?.message });
      if (isSeatMode) {
        fetchSeatMap();
      }
      return;
    }

    if (response?.data?.data?.status === 'success') {
      if (response?.data?.data?.redirect_url) {
        window.location.href = response?.data?.data?.redirect_url;
      }
      if (response?.data?.data?.widget_url) {
        navigate(`/payment/apple/?widgetUrl=${response?.data?.data?.widget_url}&eventId=${id}`);
      }
    }
  };

  const handleCloseTermsAndConditionsModal = (value) => {
    if (value) setValue('isAgreed', true, { shouldValidate: true });
    setIsTermsConditionsModalOpen(false);
  };

  // Epoint sends us here as soon as the card is charged, which can be a moment
  // before its callback reaches our server, so the order is asked about until it
  // has been settled one way or the other.
  useEffect(() => {
    if (payment !== 'success' || !ticketToken) return undefined;

    let stop = false;
    let attempts = 0;

    const ask = async () => {
      if (stop) return;
      attempts += 1;
      try {
        const response = await getOrderStatus(ticketToken);
        const order = response?.data?.data;
        if (stop) return;
        if (order?.status === 'cancelled') {
          setCancelledOrder(order);
          setIsPaymentSuccessModalOpen(false);
          return;
        }
        if (order?.status === 'pending' && attempts < 10) {
          setTimeout(ask, 2000);
        }
      } catch (error) {
        // Nothing to add for the buyer: they keep the success message and the
        // ticket link, exactly as before this check existed.
      }
    };

    ask();
    return () => { stop = true; };
  }, [payment, ticketToken]);

  const handleClosePaymentSuccessModal = () => {
    setIsPaymentSuccessModalOpen(false);
  };

  const handleClosePaymentErrorModal = () => {
    setIsPaymentErrorModalOpen(false);
  };

  const showFullPoster = coverRatio === null || coverRatio < 2.2;

  const currentPrice = (ticketDetails?.price || 0) * orderCount;
  const originalPrice = (ticketDetails?.salePrice || ticketDetails?.price || 0) * orderCount;
  const finalPrice = voucherData ? voucherData.finalAmount : currentPrice;
  const hasSalePrice = ticketDetails?.salePrice && ticketDetails?.salePrice !== ticketDetails?.price;
  const hasVoucherDiscount = voucherData && voucherData.discountAmount > 0;

  const ticketInfoButton = (
    <button
      type="button"
      onClick={() => setIsTicketInfoModalOpen(true)}
      aria-label="Biletlər haqqında"
      className="flex-shrink-0 w-12 min-h-12 self-stretch rounded-2xl bg-surface-50 hover:bg-accent-50 text-surface-400 hover:text-accent-600 flex items-center justify-center transition-all duration-200"
    >
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
      </svg>
    </button>
  );

  return (
    <Layout>
      {isTermsConditionsModalOpen && <TermsAndConditionsModal onClose={handleCloseTermsAndConditionsModal} />}
      {cancelledOrder ? (
        <PaymentConflictModal
          phone={eventData?.data?.supportPhoneNumber}
          seats={cancelledOrder?.refund?.seats?.length ? cancelledOrder.refund.seats : cancelledOrder?.seats}
          amount={cancelledOrder?.refund?.amount}
          orderRef={cancelledOrder?.orderRef}
          wasHeld={cancelledOrder?.refund?.wasHeld}
          reason={cancelledOrder?.refund?.reason}
          onClose={() => setCancelledOrder(null)}
        />
      ) : (
        isPaymentSuccessModalOpen && <PaymentSuccessModal phone={eventData?.data?.supportPhoneNumber} ticketUrl={ticketUrl} onClose={handleClosePaymentSuccessModal} />
      )}
      {isPaymentErrorModalOpen && <PaymentErrorModal phone={eventData?.data?.supportPhoneNumber} onClose={handleClosePaymentErrorModal} />}
      <TicketInfoModal eventData={eventData} isOpen={isTicketInfoModalOpen} onClose={() => setIsTicketInfoModalOpen(false)} />
      {isSeatMode && isSeatPickerOpen && (
        <SeatPickerModal
          seatMap={seatMap}
          hasError={seatMapError}
          onRetry={fetchSeatMap}
          initialSeats={selectedSeats}
          seatPricing={seatPricing}
          onConfirm={handleConfirmSeats}
          onClose={closeSeatPicker}
        />
      )}
      {lightboxIndex !== null && allPhotos.length > 0 && (
        <PhotoLightbox
          photos={allPhotos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onChangeIndex={setLightboxIndex}
        />
      )}

      {isPageLoading ? (
        <>
          {/* Skeleton Hero */}
          <div className="relative h-[50vh] md:h-[60vh] overflow-hidden bg-surface-950">
            <div className="absolute inset-0 bg-gradient-to-t from-surface-950 via-surface-900 to-surface-800" />
            <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-8 flex flex-col justify-end pb-12 pt-24">
              <div className="h-4 w-32 bg-white/10 rounded-full mb-6 animate-pulse" />
              <div className="h-12 w-3/4 bg-white/10 rounded-2xl mb-3 animate-pulse" />
              <div className="h-12 w-1/2 bg-white/10 rounded-2xl mb-6 animate-pulse" />
              <div className="flex gap-3">
                <div className="h-10 w-44 bg-white/10 rounded-xl animate-pulse" />
                <div className="h-10 w-36 bg-white/10 rounded-xl animate-pulse" />
                <div className="h-10 w-14 bg-white/10 rounded-xl animate-pulse" />
              </div>
            </div>
          </div>
          {/* Skeleton Content */}
          <div className="bg-surface-50 min-h-[50vh]">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 md:py-16">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                <div className="lg:w-[55%]">
                  <div className="bg-white rounded-3xl p-8 md:p-10 shadow-sm border border-surface-100 space-y-4 animate-pulse">
                    <div className="h-7 w-48 bg-surface-100 rounded-xl" />
                    <div className="h-4 w-full bg-surface-100 rounded-lg" />
                    <div className="h-4 w-full bg-surface-100 rounded-lg" />
                    <div className="h-4 w-5/6 bg-surface-100 rounded-lg" />
                    <div className="h-4 w-full bg-surface-100 rounded-lg" />
                    <div className="h-4 w-3/4 bg-surface-100 rounded-lg" />
                    <div className="h-4 w-full bg-surface-100 rounded-lg" />
                    <div className="h-4 w-2/3 bg-surface-100 rounded-lg" />
                  </div>
                </div>
                <div className="lg:w-[45%]">
                  <div className="bg-white rounded-3xl shadow-sm border border-surface-100 overflow-hidden animate-pulse">
                    <div className="h-20 bg-surface-200" />
                    <div className="p-8 space-y-5">
                      <div><div className="h-3 w-20 bg-surface-100 rounded mb-2" /><div className="h-12 w-full bg-surface-100 rounded-2xl" /></div>
                      <div><div className="h-3 w-16 bg-surface-100 rounded mb-2" /><div className="h-12 w-full bg-surface-100 rounded-2xl" /></div>
                      <div><div className="h-3 w-12 bg-surface-100 rounded mb-2" /><div className="h-12 w-full bg-surface-100 rounded-2xl" /></div>
                      <div><div className="h-3 w-14 bg-surface-100 rounded mb-2" /><div className="h-12 w-full bg-surface-100 rounded-2xl" /></div>
                      <div className="h-14 w-full bg-surface-100 rounded-2xl" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
      {/* Hero banner: at least half the screen, taller when the title and badges need it,
          so its content never slides up under the logo. On wide screens a banner-shaped
          crop cuts most posters in half, so the whole photo sits on the right and a
          blurred copy fills the background. Photos already banner-shaped (2.2:1 and
          wider) keep filling it. */}
      <div className="relative min-h-[50vh] md:min-h-[60vh] flex flex-col overflow-hidden bg-surface-950">
        {/* Safari starts downloading as soon as src is set, so the lazy and size hints come first */}
        <img
          sizes="100vw"
          srcSet={photoSrcSet(allPhotos[0], [640, 1280])}
          src={photoUrl(allPhotos[0], 1280)}
          alt={eventData?.data?.title}
          onLoad={(e) => setCoverRatio(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight)}
          className={`absolute inset-0 w-full h-full object-cover opacity-50 ${
            showFullPoster ? "lg:opacity-40 lg:blur-2xl lg:scale-110" : ""
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-surface-950 via-surface-950/60 to-surface-950/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-surface-950/50 to-transparent" />

        <div className="relative flex-1 w-full max-w-7xl mx-auto px-6 lg:px-8 flex items-stretch gap-10 pb-12 pt-24">
        <div className="flex-1 min-w-0 flex flex-col justify-end">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-white/50 mb-6">
            <Link to="/" className="hover:text-white transition">Ana Səhifə</Link>
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
            <span className="text-white/70">Tədbir</span>
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.1] mb-6 max-w-3xl">
            {eventData?.data?.title}
          </h1>

          {/* Meta badges */}
          <div className="flex flex-wrap items-center gap-3">
            {eventData?.data?.date && (
              <div className="glass rounded-xl px-4 py-2.5 flex items-center gap-2 text-white text-sm font-medium">
                <svg className="w-4 h-4 text-accent-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                </svg>
                {formatEventDate(eventData?.data?.date)}
              </div>
            )}
            {eventData?.data?.place && (
              <div className="glass rounded-xl px-4 py-2.5 flex items-center gap-2 text-white text-sm font-medium">
                <svg className="w-4 h-4 text-pink-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 002.273 1.765 11.842 11.842 0 00.976.544l.062.029.018.008.006.003zM10 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z" clipRule="evenodd" />
                </svg>
                {eventData?.data?.place}
              </div>
            )}
            {eventData?.data?.ageLimit && (
              <div className="glass rounded-xl px-3.5 py-2.5 text-white text-sm font-bold">
                {eventData?.data?.ageLimit}+
              </div>
            )}
          </div>

          {/* On phones the buy form comes first, so offer a jump to the description */}
          <button
            type="button"
            onClick={() => scrollToElement("about")}
            className="lg:hidden mt-4 self-start flex items-center gap-2 bg-white/95 hover:bg-white text-surface-900 text-sm font-semibold rounded-xl px-4 py-2.5 transition"
          >
            Tədbir haqqında
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </button>
        </div>

        {/* The whole photo, uncropped, whatever its shape */}
        {showFullPoster && allPhotos[0] && (
          <button
            type="button"
            onClick={() => setLightboxIndex(0)}
            aria-label="Şəkli böyüt"
            className="hidden lg:flex flex-shrink-0 max-w-[45%] items-center justify-end cursor-zoom-in"
          >
            {/* Hidden on phones; lazy, so phones never download it */}
            <img
              loading="lazy"
              src={photoUrl(allPhotos[0], 960)}
              alt={eventData?.data?.title}
              className="max-h-[calc(60vh-9rem)] max-w-full w-auto h-auto object-contain rounded-2xl shadow-2xl shadow-black/50 ring-1 ring-white/10"
            />
          </button>
        )}
        </div>
      </div>

      {/* Content area */}
      <div className="bg-surface-50 min-h-[50vh]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 md:py-16">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">

            {/* Left — Description (below the buy form on phones) */}
            <div className="order-2 lg:order-1 lg:w-[55%] space-y-8">
              <div id="about" className="bg-white rounded-3xl p-8 md:p-10 shadow-sm border border-surface-100 scroll-mt-24">
                <h2 className="text-2xl font-bold text-surface-900 mb-6">Tədbir haqqında</h2>
                <div
                  className="prose prose-sm prose-surface max-w-none text-surface-600 leading-relaxed
                    [&>p]:mb-4 [&>ul]:list-disc [&>ul]:pl-5 [&>ol]:list-decimal [&>ol]:pl-5
                    [&>h3]:text-surface-900 [&>h3]:font-semibold [&>h3]:mt-6 [&>h3]:mb-3
                    [&>strong]:text-surface-800 [&>a]:text-accent-600 [&>a:hover]:text-accent-700"
                  dangerouslySetInnerHTML={{ __html: eventData?.data?.description }}
                />
                {allPhotos.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-6">
                    {allPhotos.map((url, i) => (
                      <img
                        key={i}
                        loading="lazy"
                        src={photoUrl(url, 320)}
                        alt={`${eventData?.data?.title} - ${i + 1}`}
                        className="w-full aspect-square object-cover rounded-xl cursor-pointer hover:opacity-80 transition"
                        onClick={() => setLightboxIndex(i)}
                      />
                    ))}
                  </div>
                )}
              </div>

            </div>

            {/* Right — Form (first on phones) */}
            <div className="order-1 lg:order-2 lg:w-[45%]">
              <div className="bg-white rounded-3xl shadow-sm border border-surface-100 sticky top-24 overflow-hidden">
                {/* Form header */}
                <div className="relative px-8 py-6 bg-gradient-to-br from-surface-950 to-surface-800 text-white overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-mesh opacity-20" />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-1">
                      <svg className="w-5 h-5 text-accent-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z" />
                      </svg>
                      <h2 className="text-lg font-bold">Bilet Al</h2>
                    </div>
                    <p className="text-sm text-white/60">Təhlükəsiz ödəniş, ani bilet</p>
                  </div>
                </div>

                <form className="p-8 space-y-5" onSubmit={handleSubmit(onSubmit)}>
                  {/* Seat: its zone decides the ticket type, so there is no ticket type field.
                      Ticket types without seats (e.g. tables) are listed underneath. */}
                  {isSeatMode && (
                    <InputField label={seatlessTypes.length ? "Bilet" : "Yer"} hint={`(ən çox ${MAX_SEATS_PER_ORDER} yer)`}>
                      <input type="hidden" {...register("seatNumber", { required: isMapChoice })} />
                      <input type="hidden" {...register("ticket", { required: true })} />
                      <div className="flex gap-2">
                        {isMapChoice ? (
                          <div className="flex-1 min-w-0 flex items-center justify-between gap-3 bg-accent-50 rounded-2xl px-4 py-2.5">
                            <div className="min-w-0">
                              {selectedSeats.map(({ zone, number }) => (
                                <div key={`${zone}-${number}`} className="text-sm font-semibold text-accent-700 truncate">
                                  {zoneOf(zone)?.name || zone} · {unitLabel(zoneOf(zone), number)}
                                </div>
                              ))}
                              {ticketDetails && (
                                <div className="text-xs text-accent-600 truncate">
                                  {ticketDetails.name?.az} · {selectedSeats.length > 1
                                    ? `${selectedSeats.length} × ${ticketDetails.price}₼`
                                    : `${ticketDetails.price}₼`}
                                </div>
                              )}
                            </div>
                            {!isPast && (
                              <button
                                type="button"
                                onClick={openSeatPicker}
                                className="text-xs font-semibold text-accent-600 hover:text-accent-700 transition"
                              >
                                Dəyiş
                              </button>
                            )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={openSeatPicker}
                            disabled={isPast}
                            className={`${inputClasses} flex-1 min-w-0 flex items-center justify-between gap-3 text-left text-surface-500 hover:bg-accent-50 hover:text-accent-700 disabled:cursor-not-allowed disabled:hover:bg-surface-50 disabled:hover:text-surface-500`}
                          >
                            <span className="min-w-0 truncate">Xəritədən yer seçin</span>
                            <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
                            </svg>
                          </button>
                        )}
                        {ticketInfoButton}
                      </div>

                      {seatlessTypes.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {seatlessTypes.map((type) => {
                            const isSelected = !isMapChoice && selectedTicket === type._id;
                            return (
                              <button
                                key={type._id}
                                type="button"
                                onClick={() => chooseSeatlessType(type)}
                                disabled={isPast || type.isSoldOut}
                                aria-pressed={isSelected}
                                className={`w-full flex items-center justify-between gap-3 rounded-2xl px-4 py-3 text-left transition-all duration-200 ${
                                  isSelected ? "bg-accent-50 ring-2 ring-accent-500" : "bg-surface-50 hover:bg-surface-100"
                                } ${type.isSoldOut ? "opacity-60 cursor-not-allowed hover:bg-surface-50" : ""}`}
                              >
                                <span className="min-w-0">
                                  <span className="block text-sm font-semibold text-surface-900 truncate">{type.name?.az}</span>
                                  {type.info && <span className="block text-xs text-surface-500 line-clamp-2">{type.info}</span>}
                                </span>
                                <span className="shrink-0 text-right">
                                  <span className="block text-sm font-bold text-surface-900">{type.price}&#8380;</span>
                                  {type.isSoldOut && <span className="block text-xs text-surface-500">Bitib</span>}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </InputField>
                  )}

                  {/* Ticket Type */}
                  {!isSeatMode && (
                  <InputField label="Bilet Növü">
                    <div className="flex gap-2">
                      <select
                        id="ticket"
                        {...register("ticket", { required: true })}
                        className={`${inputClasses} flex-1`}
                      >
                        <option value="">Seçin...</option>
                        {eventData?.data?.ticketTypes?.map(({ _id, name, count, soldCount }) => {
                          // Sold out once the ticket type's limit is reached
                          const isSoldOut = Boolean(count) && (soldCount || 0) >= count;
                          return (
                            <option key={_id} value={_id} disabled={isSoldOut}>
                              {name?.az}{isSoldOut ? " — bitib" : ""}
                            </option>
                          );
                        })}
                      </select>
                      {ticketInfoButton}
                    </div>
                  </InputField>
                  )}

                  {/* Several tickets in one payment: they arrive as one email, a QR each */}
                  {selectedTicket && !isMapChoice && !isTableTicket && (
                    <InputField label="Bilet sayı">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 bg-surface-50 rounded-2xl p-1">
                          <button
                            type="button"
                            aria-label="Bilet sayını azalt"
                            disabled={isPast || quantity <= 1}
                            onClick={() => setQuantity((count) => Math.max(1, count - 1))}
                            className="w-10 h-10 rounded-xl bg-white text-lg font-bold text-surface-700 hover:bg-surface-100 disabled:text-surface-300 disabled:hover:bg-white transition"
                          >
                            −
                          </button>
                          <span aria-live="polite" className="w-10 text-center text-base font-bold text-surface-900">
                            {quantity}
                          </span>
                          <button
                            type="button"
                            aria-label="Bilet sayını artır"
                            disabled={isPast || quantity >= MAX_SEATS_PER_ORDER}
                            onClick={() => setQuantity((count) => Math.min(MAX_SEATS_PER_ORDER, count + 1))}
                            className="w-10 h-10 rounded-xl bg-white text-lg font-bold text-surface-700 hover:bg-surface-100 disabled:text-surface-300 disabled:hover:bg-white transition"
                          >
                            +
                          </button>
                        </div>
                        <p className="text-xs text-surface-500">
                          Ən çox {MAX_SEATS_PER_ORDER} bilet. Hamısı bir e-poçtla gəlir, hər biletin öz QR kodu olur.
                        </p>
                      </div>
                    </InputField>
                  )}

                  {/* Name */}
                  <InputField label="Ad, Soyad">
                    <Controller
                      control={control}
                      name="name"
                      render={() => (
                        <input
                          {...register("name", { required: true })}
                          id="name"
                          placeholder="Məsələn: Əli Həsənov"
                          className={inputClasses}
                          required
                        />
                      )}
                    />
                  </InputField>

                  {/* Email */}
                  <InputField label="Email" hint="(bilet buraya göndəriləcək)">
                    <Controller
                      control={control}
                      name="email"
                      render={() => (
                        <input
                          {...register("email", {
                            required: true,
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: "invalid email address"
                            }
                          })}
                          type="email"
                          id="email"
                          placeholder="email@domain.com"
                          className={inputClasses}
                          required
                        />
                      )}
                    />
                  </InputField>

                  {/* Phone */}
                  <InputField label="Telefon">
                    <Controller
                      control={control}
                      name="phone"
                      render={() => (
                        <ReactInputMask
                          mask="099-999-9999"
                          id="phone"
                          {...register("phone", {
                            required: true,
                            pattern: /0\d\d-\d\d\d-\d\d\d\d/
                          })}
                          placeholder="(0XX) XXX-XX-XX"
                          className={inputClasses}
                          required
                        />
                      )}
                    />
                  </InputField>

                  {/* Voucher */}
                  <InputField label="Promokod" hint="(əgər varsa)">
                    <div className="relative">
                      <input
                        {...register("voucherCode")}
                        id="voucherCode"
                        placeholder="Kod daxil edin"
                        className={inputClasses}
                      />
                      {isValidatingVoucher && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-accent-200 border-t-accent-600" />
                        </div>
                      )}
                    </div>

                    {voucherError && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2">
                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {voucherError}
                      </div>
                    )}

                    {voucherData && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2">
                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {voucherData.voucher.name?.az || voucherData.voucher.code} &mdash; {voucherData.discountAmount}&#8380; endirim
                      </div>
                    )}
                  </InputField>

                  {/* The organizer's own question */}
                  {customField && (
                    <InputField label={customField.label} hint={customField.isRequired ? undefined : "(istəyə bağlı)"}>
                      <Controller
                        control={control}
                        name="customValue"
                        defaultValue=""
                        rules={{ required: customField.isRequired }}
                        render={({ field }) => (
                          <SearchableSelect
                            id="customValue"
                            options={customField.options}
                            value={field.value || ""}
                            onChange={field.onChange}
                            required={customField.isRequired}
                            placeholder="Seçin"
                            inputClassName={inputClasses}
                          />
                        )}
                      />
                      {errors?.customValue && (
                        <div className="mt-2 text-xs text-red-600">{customField.label} seçin</div>
                      )}
                    </InputField>
                  )}

                  {/* Divider */}
                  <div className="border-t border-surface-100 pt-5">
                    <label className="block text-sm font-semibold text-surface-900 mb-3">Ödəmə metodu</label>
                    <Controller
                      control={control}
                      name="paymentMethod"
                      defaultValue="card"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <div className="flex flex-col gap-2">
                          {eventData?.data?.paymentModes?.map((mode) => {
                            if (mode?.type === 'apple-pay' && !isIOS) return null;
                            if (mode?.type === 'google-pay' && !isAndroid) return null;
                            return (
                              <label
                                key={mode?.type}
                                className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl cursor-pointer transition-all duration-200 ${
                                  field.value === mode?.type
                                    ? 'bg-accent-50 ring-2 ring-accent-500'
                                    : 'bg-surface-50 hover:bg-surface-100'
                                }`}
                              >
                                <input
                                  type="radio"
                                  value={mode?.type}
                                  checked={field.value === mode?.type}
                                  onChange={field.onChange}
                                  className="form-radio h-4 w-4 text-accent-600 border-surface-300 focus:ring-accent-500"
                                />
                                <div dangerouslySetInnerHTML={{ __html: mode?.icon }} className="h-5 w-5 text-surface-600" />
                                <span className="text-sm font-medium text-surface-700">{mode?.name?.az}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    />
                  </div>

                  {errors?.root?.serverError && (
                    <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-2xl border border-red-100">
                      {errors.root.serverError.message || "Server xətası baş verdi. Zəhmət olmasa yenidən cəhd edin."}
                    </div>
                  )}

                  {/* Terms */}
                  <div className="flex items-start gap-3">
                    <input
                      {...register("isAgreed", { required: true })}
                      id="checked-checkbox-agreement"
                      type="checkbox"
                      className="w-4 h-4 mt-0.5 text-accent-600 bg-surface-50 border-surface-300 rounded focus:ring-accent-500 focus:ring-2"
                    />
                    <label htmlFor="checked-checkbox-agreement" className="text-sm text-surface-500 leading-relaxed">
                      <button
                        type="button"
                        className="text-accent-600 hover:text-accent-700 font-medium transition"
                        onClick={() => setIsTermsConditionsModalOpen(true)}
                      >
                        Şərtlər və qaydalar
                      </button>
                      -ı oxudum və qəbul edirəm.
                    </label>
                  </div>

                  {/* Submit */}
                  {isPast && (
                    <div className="w-full bg-surface-100 text-surface-500 py-4 rounded-2xl font-semibold text-center text-sm">
                      Bu tədbir artıq keçib. Bilet satışı başa çatıb.
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={!isValid || isSubmitting || isPast}
                    className={`w-full relative overflow-hidden bg-gradient-to-r from-accent-600 to-pink-600 text-white py-4 rounded-2xl font-bold disabled:from-surface-200 disabled:to-surface-300 disabled:text-surface-500 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 text-base transition-all duration-300 hover:shadow-[0_20px_40px_-10px_rgba(139,92,246,0.4)] disabled:hover:shadow-none active:scale-[0.98] ${isPast ? 'hidden' : ''}`}
                  >
                    {selectedTicket ? (
                      <>
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                            Gözləyin...
                          </span>
                        ) : (
                          <>
                            <span>Ödə</span>
                            <span className="w-px h-4 bg-white/30" />
                            {(hasSalePrice || hasVoucherDiscount) && (
                              <span className="line-through text-white/50 text-sm">
                                {hasSalePrice ? originalPrice : currentPrice}&#8380;
                              </span>
                            )}
                            <span className="font-extrabold text-lg">
                              {finalPrice}&#8380;
                            </span>
                            {hasVoucherDiscount && (
                              <span className="bg-white/20 rounded-full px-2 py-0.5 text-xs font-semibold">
                                -{voucherData.discountAmount}&#8380;
                              </span>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <span className="text-surface-400">{isSeatMode ? (seatlessTypes.length ? "Bilet seçin" : "Yer seçin") : "Bilet növü seçin"}</span>
                    )}
                  </button>

                  {/* Trust row */}
                  <div className="flex items-center justify-center gap-5 pt-1">
                    <div className="flex items-center gap-1.5 text-[11px] text-surface-400">
                      <svg className="w-3.5 h-3.5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
                      </svg>
                      256-bit SSL
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] text-surface-400">
                      <svg className="w-3.5 h-3.5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                      </svg>
                      Təhlükəsiz ödəniş
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] text-surface-400">
                      <svg className="w-3.5 h-3.5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-5a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                      </svg>
                      Ani bilet
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
        </>
      )}
    </Layout>
  );
};

export default PlanDetailsPage;
